import { useState } from "react";
import {
  AlarmClock,
  AlarmClockOff,
  ArrowRightLeft,
  Ban,
  Copy,
  CopyPlus,
  MoreHorizontal,
  RotateCcw,
  Star,
  StarOff,
  Trash2,
} from "lucide-react";
import { PRIORITY_LABEL, STATUS_LABEL, TASK_STATUSES, type Priority, type TaskStatus, type TaskWithFlags } from "@desk/contracts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { useTaskActions } from "@/data/actions";
import { CustomSnoozeDialog, SnoozePresetItems } from "@/features/snooze/SnoozeControls";
import { MoveDialog } from "@/features/destination/MoveDialog";
import { copyText } from "@/lib/clipboard";
import { duplicateAndOpen } from "./duplicate";

export const PRIORITY_ORDER: Priority[] = ["urgent", "high", "medium", "low", "none"];
export const PRIORITY_KEYS: Record<Priority, string> = { urgent: "1", high: "2", medium: "3", low: "4", none: "0" };

export function StatusRadioItems({ value, onChange }: { value: TaskStatus; onChange: (s: TaskStatus) => void }) {
  return (
    <DropdownMenuRadioGroup value={value} onValueChange={(v) => onChange(v as TaskStatus)}>
      {TASK_STATUSES.map((s) => (
        <DropdownMenuRadioItem key={s} value={s}>
          <StatusIcon status={s} />
          {STATUS_LABEL[s]}
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );
}

export function PriorityRadioItems({ value, onChange }: { value: Priority; onChange: (p: Priority) => void }) {
  return (
    <DropdownMenuRadioGroup value={value} onValueChange={(v) => onChange(v as Priority)}>
      {PRIORITY_ORDER.map((p) => (
        <DropdownMenuRadioItem key={p} value={p} shortcut={PRIORITY_KEYS[p]}>
          <PriorityIcon priority={p} />
          {PRIORITY_LABEL[p]}
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );
}

/** The "…" task menu used by rows, cards and detail. `statusFirst` puts "Move to…" statuses on top (board). */
export function TaskMenu({ task, statusFirst, align = "end" }: { task: TaskWithFlags; statusFirst?: boolean; align?: "start" | "end" }) {
  const a = useTaskActions();
  const [customOpen, setCustomOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const closed = task.status === "done" || task.status === "cancelled";
  const planned = a.isPlannedToday(task);
  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon-sm"
            variant="quiet"
            aria-label={`Actions for ${task.title}`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {statusFirst && (
            <>
              <div className="px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">Move to</div>
              <StatusRadioItems value={task.status} onChange={(s) => void a.setStatus(task, s)} />
              <DropdownMenuSeparator />
            </>
          )}
          {!statusFirst && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <StatusIcon status={task.status} />
                Status
                <span className="ml-auto pl-4 text-xs text-subtle">S</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <StatusRadioItems value={task.status} onChange={(s) => void a.setStatus(task, s)} />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <PriorityIcon priority={task.priority} />
              Priority
              <span className="ml-auto pl-4 text-xs text-subtle">P</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <PriorityRadioItems value={task.priority} onChange={(p) => void a.setPriority(task, p)} />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          {!closed && (
            <DropdownMenuItem shortcut="T" onSelect={() => void a.toggleToday(task)}>
              {planned ? <StarOff /> : <Star />}
              {planned ? "Remove from Today" : "Add to Today"}
            </DropdownMenuItem>
          )}
          {task.flags.isSnoozed ? (
            <DropdownMenuItem onSelect={() => void a.unsnooze(task)}>
              <AlarmClockOff />
              Unsnooze
            </DropdownMenuItem>
          ) : (
            !closed && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <AlarmClock />
                  Snooze
                  <span className="ml-auto pl-4 text-xs text-subtle">H</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <SnoozePresetItems onPick={(iso) => void a.snooze(task, iso)} onCustom={() => setCustomOpen(true)} />
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )
          )}
          <DropdownMenuItem onSelect={() => setMoveOpen(true)}>
            <ArrowRightLeft />
            Move to…
          </DropdownMenuItem>
          <DropdownMenuItem shortcut="⌘D" onSelect={() => void duplicateAndOpen(a, task)}>
            <CopyPlus />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              void copyText(task.readableId, `Copied ${task.readableId}`);
            }}
          >
            <Copy />
            Copy ID
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {closed ? (
            <DropdownMenuItem onSelect={() => void a.reopen(task)}>
              <RotateCcw />
              Reopen
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => void a.cancel(task)}>
              <Ban />
              Cancel task
            </DropdownMenuItem>
          )}
          <DropdownMenuItem destructive shortcut="⌘⌫" onSelect={() => void a.remove(task)}>
            <Trash2 />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CustomSnoozeDialog open={customOpen} onOpenChange={setCustomOpen} onConfirm={(iso) => void a.snooze(task, iso)} />
      <MoveDialog task={task} open={moveOpen} onOpenChange={setMoveOpen} />
    </>
  );
}
